import React from 'react';
import ProfileAssistantRow from './ProfileAssistantRow';
import ProfileUserAnswerRow from './ProfileUserAnswerRow';
import { hasStoredResidenceCard, normalizeResidenceCardNumber } from './useGeneralProfileFlow';

function displayMiddle(v: string) {
  const t = v.trim();
  return t.length > 0 ? t : '-';
}

type EditStep =
  | 'legal'
  | 'kana'
  | 'dob'
  | 'gender'
  | 'nationality'
  | 'phone'
  | 'homePhone'
  | 'address'
  | 'email'
  | 'residenceCard'
  | 'myNumber'
  | 'maritalStatus'
  | 'familyStatus'
  | 'disabilityCategory'
  | 'disabilityFacts'
  | 'emergencyContact'
  | 'emergencyName'
  | 'emergencyRelationship'
  | 'emergencyPhone'
  | 'emergencyAddress';

export interface GeneralProfileChatThreadProps {
  t: (key: string) => string;
  introRevealed: number;
  step: string;
  threadEndRef: React.RefObject<HTMLDivElement | null>;
  legalFamily: string;
  legalFirst: string;
  legalMiddle: string;
  kanaFamily: string;
  kanaFirst: string;
  kanaMiddle: string;
  dobDisplayFormatted: string;
  genderLabel: string;
  nationalityLabel: string;
  phoneNumber: string;
  homePhoneDisplay: string;
  address: { postalCode: string; pref: string; city: string; town: string; line2: string };
  email: string;
  residenceCardNumber: string;
  myNumber: string;
  myNumberDisplay: string;
  showResidenceCardStep: boolean;
  maritalStatusLabel: string;
  familyStatusDisplay: string;
  disabilityCategoryLabel: string;
  disabilityFacts: string;
  emergencyHasContact: 'yes' | 'no' | '';
  emergencyContactLabel: string;
  emergencyName: string;
  emRelationship: string;
  emergencyPhone: string;
  emergencyAddress: { postalCode: string; pref: string; city: string; town: string; line2: string };
  legalCommitted: boolean;
  kanaCommitted: boolean;
  dobCommitted: boolean;
  genderCommitted: boolean;
  nationalityCommitted: boolean;
  phoneCommitted: boolean;
  homePhoneCommitted: boolean;
  addressCommitted: boolean;
  emailCommitted: boolean;
  residenceCardCommitted: boolean;
  myNumberCommitted: boolean;
  maritalStatusCommitted: boolean;
  familyStatusCommitted: boolean;
  disabilityCategoryCommitted: boolean;
  disabilityFactsCommitted: boolean;
  emergencyContactCommitted: boolean;
  emergencyNameCommitted: boolean;
  emRelationshipCommitted: boolean;
  emergencyPhoneCommitted: boolean;
  emergencyAddressCommitted: boolean;
  onUndo: () => void;
  onEdit: (step: EditStep) => void;
  showUndoLegal: boolean;
  showUndoKana: boolean;
  showUndoDob: boolean;
  showUndoGender: boolean;
  showUndoNationality: boolean;
  showUndoPhone: boolean;
  showUndoHomePhone: boolean;
  showUndoAddress: boolean;
  showUndoEmail: boolean;
  showUndoResidenceCard: boolean;
  showUndoMyNumber: boolean;
  showUndoMaritalStatus: boolean;
  showUndoFamilyStatus: boolean;
  showUndoDisabilityCategory: boolean;
  showUndoDisabilityFacts: boolean;
  showUndoEmergencyContact: boolean;
  showUndoEmergencyName: boolean;
  showUndoEmergencyRelationship: boolean;
  showUndoEmergencyPhone: boolean;
  showUndoEmergencyAddress?: boolean;
  signatureImageUrl?: string | null;
  onRedoSignature?: () => void;
}

function Answer({
  children,
  editStep,
  showUndo,
  onEdit,
  onUndo,
  editLabel,
  undoLabel,
}: {
  children: React.ReactNode;
  editStep: EditStep;
  showUndo: boolean;
  onEdit: (s: EditStep) => void;
  onUndo: () => void;
  editLabel: string;
  undoLabel: string;
}) {
  return (
    <ProfileUserAnswerRow
      showEdit
      onEdit={() => onEdit(editStep)}
      editLabel={editLabel}
      showUndo={showUndo}
      onUndo={onUndo}
      undoLabel={undoLabel}
    >
      {children}
    </ProfileUserAnswerRow>
  );
}

export default function GeneralProfileChatThread(p: GeneralProfileChatThreadProps) {
  const editLabel = p.t('submitDocuments.fillFormsChecklist.edit');
  const undoLabel = p.t('submitDocuments.chat.undo');
  const a = {
    onEdit: p.onEdit,
    onUndo: p.onUndo,
    editLabel,
    undoLabel,
  };
  const addr = (x: typeof p.address) =>
    `〒${x.postalCode} ${x.pref}${x.city}${x.town}${x.line2 ? ` ${x.line2}` : ''}`;

  return (
    <div className="gen-profile-chat__thread">
      {p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.intro')}</ProfileAssistantRow>
      )}
      {p.legalCommitted && (
        <Answer editStep="legal" showUndo={p.showUndoLegal} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.family')}: {p.legalFamily}</div>
          <div>{p.t('submitDocuments.profileName.bubble.first')}: {p.legalFirst}</div>
          <div>{p.t('submitDocuments.profileName.bubble.middle')}: {displayMiddle(p.legalMiddle)}</div>
        </Answer>
      )}
      {(p.step === 'kana' || p.kanaCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.kanaIntro')}</ProfileAssistantRow>
      )}
      {p.kanaCommitted && (
        <Answer editStep="kana" showUndo={p.showUndoKana} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.kanaFamily')}: {p.kanaFamily}</div>
          <div>{p.t('submitDocuments.profileName.bubble.kanaFirst')}: {p.kanaFirst}</div>
          <div>{p.t('submitDocuments.profileName.bubble.kanaMiddle')}: {displayMiddle(p.kanaMiddle)}</div>
        </Answer>
      )}
      {(p.step === 'dob' || p.dobCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.dobIntro')}</ProfileAssistantRow>
      )}
      {p.dobCommitted && (
        <Answer editStep="dob" showUndo={p.showUndoDob} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.dob')}: {p.dobDisplayFormatted}</div>
        </Answer>
      )}
      {(p.step === 'gender' || p.genderCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.genderIntro')}</ProfileAssistantRow>
      )}
      {p.genderCommitted && (
        <Answer editStep="gender" showUndo={p.showUndoGender} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.gender')}: {p.genderLabel}</div>
        </Answer>
      )}
      {(p.step === 'nationality' || p.nationalityCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.nationalityIntro')}</ProfileAssistantRow>
      )}
      {p.nationalityCommitted && (
        <Answer editStep="nationality" showUndo={p.showUndoNationality} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.nationality')}: {p.nationalityLabel}</div>
        </Answer>
      )}
      {(p.step === 'phone' || p.phoneCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.phoneIntro')}</ProfileAssistantRow>
      )}
      {p.phoneCommitted && (
        <Answer editStep="phone" showUndo={p.showUndoPhone} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.phone')}: {p.phoneNumber}</div>
        </Answer>
      )}
      {(p.step === 'homePhone' || p.homePhoneCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.homePhoneIntro')}</ProfileAssistantRow>
      )}
      {p.homePhoneCommitted && (
        <Answer editStep="homePhone" showUndo={p.showUndoHomePhone} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.homePhone')}: {p.homePhoneDisplay}</div>
        </Answer>
      )}
      {(p.step === 'address' || p.addressCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.addressIntro')}</ProfileAssistantRow>
      )}
      {p.addressCommitted && (
        <Answer editStep="address" showUndo={p.showUndoAddress} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.address')}: {addr(p.address)}</div>
        </Answer>
      )}
      {(p.step === 'email' || p.emailCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.emailIntro')}</ProfileAssistantRow>
      )}
      {p.emailCommitted && (
        <Answer editStep="email" showUndo={p.showUndoEmail} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.email')}: {p.email}</div>
        </Answer>
      )}
      {p.showResidenceCardStep && (p.step === 'residenceCard' || p.residenceCardCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.residenceCardIntro')}</ProfileAssistantRow>
      )}
      {p.showResidenceCardStep && p.residenceCardCommitted && hasStoredResidenceCard(p.residenceCardNumber) && (
        <Answer editStep="residenceCard" showUndo={p.showUndoResidenceCard} {...a}>
          <div>
            {p.t('submitDocuments.profileName.bubble.residenceCardNumber')}:{' '}
            {normalizeResidenceCardNumber(p.residenceCardNumber)}
          </div>
        </Answer>
      )}
      {(p.step === 'myNumber' || p.myNumberCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.myNumberIntro')}</ProfileAssistantRow>
      )}
      {p.myNumberCommitted && (
        <Answer editStep="myNumber" showUndo={p.showUndoMyNumber} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.myNumber')}: {p.myNumberDisplay}</div>
        </Answer>
      )}
      {(p.step === 'maritalStatus' || p.maritalStatusCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.maritalStatusIntro')}</ProfileAssistantRow>
      )}
      {p.maritalStatusCommitted && (
        <Answer editStep="maritalStatus" showUndo={p.showUndoMaritalStatus} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.maritalStatus')}: {p.maritalStatusLabel}</div>
        </Answer>
      )}
      {(p.step === 'familyStatus' || p.familyStatusCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.familyStatusIntro')}</ProfileAssistantRow>
      )}
      {p.familyStatusCommitted && (
        <Answer editStep="familyStatus" showUndo={p.showUndoFamilyStatus} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.familyStatus')}: {p.familyStatusDisplay}</div>
        </Answer>
      )}
      {(p.step === 'disabilityCategory' || p.disabilityCategoryCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>
          {p.t('submitDocuments.profileName.disabilityCategoryIntro')}
        </ProfileAssistantRow>
      )}
      {p.disabilityCategoryCommitted && (
        <Answer editStep="disabilityCategory" showUndo={p.showUndoDisabilityCategory} {...a}>
          <div>
            {p.t('submitDocuments.profileName.bubble.disabilityCategory')}: {p.disabilityCategoryLabel}
          </div>
        </Answer>
      )}
      {(p.step === 'disabilityFacts' || p.disabilityFactsCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.disabilityFactsIntro')}</ProfileAssistantRow>
      )}
      {p.disabilityFactsCommitted && (
        <Answer editStep="disabilityFacts" showUndo={p.showUndoDisabilityFacts} {...a}>
          <div>{p.t('submitDocuments.profileName.bubble.disabilityFacts')}: {p.disabilityFacts}</div>
        </Answer>
      )}
      {(p.step === 'emergencyContact' || p.emergencyContactCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.emergency.gateIntro')}</ProfileAssistantRow>
      )}
      {p.emergencyContactCommitted && (
        <Answer editStep="emergencyContact" showUndo={p.showUndoEmergencyContact} {...a}>
          <div>{p.t('submitDocuments.profileName.emergency.bubble.gate')}: {p.emergencyContactLabel}</div>
        </Answer>
      )}
      {p.emergencyHasContact === 'yes' && (p.step === 'emergencyName' || p.emergencyNameCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.emergency.nameIntro')}</ProfileAssistantRow>
      )}
      {p.emergencyHasContact === 'yes' && p.emergencyNameCommitted && (
        <Answer editStep="emergencyName" showUndo={p.showUndoEmergencyName} {...a}>
          <div>{p.t('submitDocuments.profileName.emergency.bubble.name')}: {p.emergencyName}</div>
        </Answer>
      )}
      {p.emergencyHasContact === 'yes' &&
        (p.step === 'emergencyRelationship' || p.emRelationshipCommitted) &&
        p.introRevealed >= 1 && (
          <ProfileAssistantRow animateIn>
            {p.t('submitDocuments.profileName.emergency.relationshipIntro')}
          </ProfileAssistantRow>
        )}
      {p.emergencyHasContact === 'yes' && p.emRelationshipCommitted && (
        <Answer editStep="emergencyRelationship" showUndo={p.showUndoEmergencyRelationship} {...a}>
          <div>{p.t('submitDocuments.profileName.emergency.bubble.relationship')}: {p.emRelationship}</div>
        </Answer>
      )}
      {p.emergencyHasContact === 'yes' && (p.step === 'emergencyPhone' || p.emergencyPhoneCommitted) && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.emergency.phoneIntro')}</ProfileAssistantRow>
      )}
      {p.emergencyHasContact === 'yes' && p.emergencyPhoneCommitted && (
        <Answer editStep="emergencyPhone" showUndo={p.showUndoEmergencyPhone} {...a}>
          <div>{p.t('submitDocuments.profileName.emergency.bubble.phone')}: {p.emergencyPhone}</div>
        </Answer>
      )}
      {p.emergencyHasContact === 'yes' &&
        (p.step === 'emergencyAddress' || p.emergencyAddressCommitted) &&
        p.introRevealed >= 1 && (
          <ProfileAssistantRow animateIn>
            {p.t('submitDocuments.profileName.emergency.addressIntro')}
          </ProfileAssistantRow>
        )}
      {p.emergencyHasContact === 'yes' && p.emergencyAddressCommitted && (
        <Answer editStep="emergencyAddress" showUndo={Boolean(p.showUndoEmergencyAddress)} {...a}>
          <div>{p.t('submitDocuments.profileName.emergency.bubble.address')}: {addr(p.emergencyAddress)}</div>
        </Answer>
      )}
      {p.step === 'finalConfirm' && p.introRevealed >= 1 && (
        <ProfileAssistantRow animateIn>{p.t('submitDocuments.profileName.finalConfirm')}</ProfileAssistantRow>
      )}
      {p.signatureImageUrl && (
        <div className="gen-profile-chat__row gen-profile-chat__row--user gen-profile-chat__row--enter">
          <div className="gen-profile-chat__user-bubble-wrap">
            <div className="gen-profile-chat__bubble gen-profile-chat__bubble--user">
              <div className="gen-profile-chat__user-lines">
                <div>{p.t('submitDocuments.profileName.signature.label')}</div>
                <img
                  src={p.signatureImageUrl}
                  alt={p.t('submitDocuments.profileName.signature.imageAlt')}
                  className="gen-profile-chat__signature-img"
                />
              </div>
            </div>
            {p.onRedoSignature && (
              <button type="button" className="gen-profile-chat__edit" onClick={p.onRedoSignature}>
                {p.t('submitDocuments.profileName.signature.redo')}
              </button>
            )}
          </div>
        </div>
      )}
      <div ref={p.threadEndRef} />
    </div>
  );
}
